/**
 * Post-Publish Change Alert
 * 
 * Shows alerts when a Published Priority List has been modified.
 * Allows acknowledging changes to notify downstream systems.
 */

import React from 'react';
import { 
  AlertTriangle,
  Plus,
  Minus,
  ArrowUpDown,
  Scissors,
  Settings,
  Check,
  Clock,
  User,
  ChevronDown,
  ChevronUp,
  Bell,
} from 'lucide-react';
import type { FocusList, PostPublishChange, PostPublishChangeType } from '../../../types/prioritization/lifecycle';

// ============================================================================
// Props
// ============================================================================

interface PostPublishChangeAlertProps {
  /** The priority list with changes */
  focusList: FocusList;
  
  /** Callback when user acknowledges a change */
  onAcknowledge?: (changeId: string) => void;
  
  /** Callback when user acknowledges all changes */
  onAcknowledgeAll?: () => void;
  
  /** Whether to show in compact mode */
  compact?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const CHANGE_TYPE_CONFIG: Record<PostPublishChangeType, {
  label: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
}> = {
  priority_added: {
    label: 'Priority Added',
    icon: <Plus className="w-4 h-4" />,
    color: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    bgColor: 'bg-[var(--green-dim)]',
  },
  priority_removed: {
    label: 'Priority Removed',
    icon: <Minus className="w-4 h-4" />,
    color: 'text-[var(--coral)]',
    bgColor: 'bg-[var(--coral-dim)]',
  },
  priority_reordered: {
    label: 'Priorities Reordered',
    icon: <ArrowUpDown className="w-4 h-4" />,
    color: 'text-[var(--text-ter)]',
    bgColor: 'bg-[var(--slate-dim)]',
  },
  cutline_moved: {
    label: 'Cut Line Moved',
    icon: <Scissors className="w-4 h-4" />,
    color: 'text-[var(--mustard)]',
    bgColor: 'bg-[var(--mustard-dim)]',
  },
  criteria_changed: {
    label: 'Criteria Changed',
    icon: <Settings className="w-4 h-4" />,
    color: 'text-[var(--text-ter)]',
    bgColor: 'bg-[var(--slate-dim)]',
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

function formatDate(date: Date): string {
  return new Date(date).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ============================================================================
// Sub-Components
// ============================================================================

interface ChangeItemProps {
  change: PostPublishChange;
  onAcknowledge?: () => void;
  expanded?: boolean;
  onToggle?: () => void;
}

function ChangeItem({ change, onAcknowledge, expanded, onToggle }: ChangeItemProps) {
  const config = CHANGE_TYPE_CONFIG[change.type];
  const isAcknowledged = !!change.acknowledgedAt;
  
  return (
    <div className={` border ${isAcknowledged ? 'border-[var(--rule)] bg-[var(--surface)]' : 'border-[var(--mustard)] bg-[var(--mustard-dim)]'}`}>
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center gap-3 text-left"
      >
        <div className={`p-2  ${config.bgColor} ${config.color}`}>
          {config.icon}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`font-medium ${isAcknowledged ? 'text-[var(--text-ter)]' : 'text-[var(--text-pri)]'}`}>
              {config.label}
            </span>
            {isAcknowledged && (
              <span className="text-xs text-[var(--green)] dark:text-[var(--text-ter)] flex items-center gap-1">
                <Check className="w-3 h-3" />
                Acknowledged
              </span>
            )}
          </div>
          <p className={`text-sm truncate ${isAcknowledged ? 'text-[var(--text-ter)]' : 'text-[var(--text-sec)]'}`}>
            {change.description}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-xs text-[var(--text-ter)]">
            {formatDate(change.detectedAt)}
          </span>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-[var(--text-ter)]" />
          ) : (
            <ChevronDown className="w-4 h-4 text-[var(--text-ter)]" />
          )}
        </div>
      </button>
      
      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t border-[var(--rule)]">
          <div className="mt-3 space-y-2 text-sm">
            <div className="flex items-center gap-2 text-[var(--text-ter)]">
              <Clock className="w-4 h-4" />
              <span>Detected: {formatDate(change.detectedAt)}</span>
            </div>
            
            {change.acknowledgedAt && change.acknowledgedBy && (
              <div className="flex items-center gap-2 text-[var(--text-ter)]">
                <User className="w-4 h-4" />
                <span>Acknowledged by {change.acknowledgedBy} at {formatDate(change.acknowledgedAt)}</span>
              </div>
            )}
            
            {change.affectedPriorityIds && change.affectedPriorityIds.length > 0 && (
              <div className="text-[var(--text-ter)]">
                <span className="font-medium">Affected items:</span> {change.affectedPriorityIds.length}
              </div>
            )}
            
            {!isAcknowledged && onAcknowledge && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onAcknowledge();
                }}
                className="mt-2 w-full px-3 py-2 bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] transition-colors flex items-center justify-center gap-2"
              >
                <Check className="w-4 h-4" />
                Acknowledge Change
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function PostPublishChangeAlert({
  focusList,
  onAcknowledge,
  onAcknowledgeAll,
  compact = false,
}: PostPublishChangeAlertProps) {
  const [expandedId, setExpandedId] = React.useState<string | null>(null);
  
  const changes = focusList.postPublishChanges || [];
  const unacknowledgedChanges = changes.filter(c => !c.acknowledgedAt);
  
  // Don't render if no changes
  if (changes.length === 0) {
    return null;
  }
  
  // Compact view - just a warning banner
  if (compact) {
    if (unacknowledgedChanges.length === 0) {
      return null;
    }
    
    return (
      <div className="flex items-center gap-3 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
        <Bell className="w-5 h-5 text-[var(--mustard)]" />
        <div className="flex-1">
          <span className="text-sm font-medium text-[var(--mustard)]">
            {unacknowledgedChanges.length} change{unacknowledgedChanges.length !== 1 ? 's' : ''} since publication
          </span>
        </div>
        {onAcknowledgeAll && (
          <button
            onClick={onAcknowledgeAll}
            className="text-sm text-[var(--mustard)] hover:text-[var(--mustard)] font-medium"
          >
            Acknowledge All
          </button>
        )}
      </div>
    );
  }
  
  // Full view
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)]">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[var(--rule)] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-[var(--mustard)]" />
          <h3 className="font-medium text-[var(--text-pri)]">Post-Publication Changes</h3>
          {unacknowledgedChanges.length > 0 && (
            <span className="px-2 py-0.5 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded-full text-xs font-medium">
              {unacknowledgedChanges.length} unacknowledged
            </span>
          )}
        </div>
        
        {unacknowledgedChanges.length > 1 && onAcknowledgeAll && (
          <button
            onClick={onAcknowledgeAll}
            className="text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium flex items-center gap-1"
          >
            <Check className="w-4 h-4" />
            Acknowledge All
          </button>
        )}
      </div>
      
      {/* Changes List */}
      <div className="p-4 space-y-3">
        {changes.length === 0 ? (
          <p className="text-sm text-[var(--text-ter)] text-center py-4">
            No changes since publication
          </p>
        ) : (
          <>
            {/* Unacknowledged first */}
            {unacknowledgedChanges.map(change => (
              <ChangeItem
                key={change.id}
                change={change}
                expanded={expandedId === change.id}
                onToggle={() => setExpandedId(expandedId === change.id ? null : change.id)}
                onAcknowledge={onAcknowledge ? () => onAcknowledge(change.id) : undefined}
              />
            ))}
            
            {/* Acknowledged changes */}
            {changes.filter(c => c.acknowledgedAt).length > 0 && (
              <details className="mt-4">
                <summary className="text-sm text-[var(--text-ter)] cursor-pointer hover:text-[var(--text-sec)]">
                  Show {changes.filter(c => c.acknowledgedAt).length} acknowledged change(s)
                </summary>
                <div className="mt-3 space-y-3">
                  {changes.filter(c => c.acknowledgedAt).map(change => (
                    <ChangeItem
                      key={change.id}
                      change={change}
                      expanded={expandedId === change.id}
                      onToggle={() => setExpandedId(expandedId === change.id ? null : change.id)}
                    />
                  ))}
                </div>
              </details>
            )}
          </>
        )}
      </div>
      
      {/* Footer with explanation */}
      {unacknowledgedChanges.length > 0 && (
        <div className="px-4 py-3 bg-[var(--surface)] border-t border-[var(--rule)] text-sm text-[var(--text-ter)]">
          <p>
            These changes occurred after this list was published. 
            Acknowledging them indicates you've reviewed the changes and 
            notified any downstream planning systems if necessary.
          </p>
        </div>
      )}
    </div>
  );
}

export default PostPublishChangeAlert;
